import React, { useState, useEffect } from 'react';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import Select from 'components/ui/Select';
import Icon from 'components/AppIcon';
import { productCatalogService } from 'services/productCatalogService';

const SupplierPanel = ({ product, onClose }) => {
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    providerId: '', supplierSku: '', costPrice: '', availableStock: '', leadTimeDays: '',
  });

  useEffect(() => {
    if (product?.id) {
      loadData();
    }
  }, [product?.id]);

  const loadData = async () => {
    setLoading(true);
    const [sp, prov] = await Promise.all([
      productCatalogService?.getSupplierProducts(product?.id),
      productCatalogService?.getProviders(),
    ]);
    setSupplierProducts(sp);
    setProviders(prov);
    setLoading(false);
  };

  const handleAddSupplier = async (e) => {
    e?.preventDefault();
    if (!form?.providerId) return;
    setSaving(true);
    try {
      await productCatalogService?.upsertSupplierProduct({
        providerId: form?.providerId,
        productId: product?.id,
        supplierSku: form?.supplierSku,
        costPrice: parseFloat(form?.costPrice) || 0,
        availableStock: form?.availableStock ? parseFloat(form?.availableStock) : null,
        leadTimeDays: form?.leadTimeDays ? parseFloat(form?.leadTimeDays) : null,
      });
      setForm({ providerId: '', supplierSku: '', costPrice: '', availableStock: '', leadTimeDays: '' });
      setShowAddForm(false);
      await loadData();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('¿Desactivar esta relación con el proveedor?')) return;
    await productCatalogService?.removeSupplierProduct(id);
    await loadData();
  };

  const providerOptions = providers?.map(p => ({ value: p?.id, label: p?.name }));

  return (
    <div className="border border-border rounded-lg bg-muted/20 p-4 mt-2">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Building2" size={18} className="text-primary" />
          <h3 className="font-semibold text-foreground">Proveedores — {product?.name}</h3>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-muted">
          <Icon name="X" size={16} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-4 text-muted-foreground">
          <Icon name="Loader2" size={16} className="animate-spin" />
          <span className="text-sm">Cargando...</span>
        </div>
      ) : (
        <>
          {supplierProducts?.length > 0 ? (
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">Proveedor</th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">SKU Proveedor</th>
                    <th className="text-right py-2 px-3 text-muted-foreground font-medium">Costo</th>
                    <th className="text-right py-2 px-3 text-muted-foreground font-medium">Existencia</th>
                    <th className="text-right py-2 px-3 text-muted-foreground font-medium">Días Entrega</th>
                    <th className="text-center py-2 px-3 text-muted-foreground font-medium">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {supplierProducts?.map(sp => (
                    <tr key={sp?.id} className="border-b border-border/50">
                      <td className="py-2 px-3 font-medium">{sp?.provider?.name || '—'}</td>
                      <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{sp?.supplierSku || '—'}</td>
                      <td className="py-2 px-3 text-right">${parseFloat(sp?.costPrice || 0)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                      <td className="py-2 px-3 text-right">{sp?.availableStock ?? '—'}</td>
                      <td className="py-2 px-3 text-right">{sp?.leadTimeDays ?? '—'}</td>
                      <td className="py-2 px-3 text-center">
                        <Button variant="ghost" size="xs" onClick={() => handleRemove(sp?.id)}>
                          <Icon name="Trash2" size={13} className="text-error" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground mb-4">Sin proveedores vinculados.</p>
          )}

          {!showAddForm ? (
            <Button variant="outline" size="sm" onClick={() => setShowAddForm(true)}>
              <Icon name="Plus" size={14} className="mr-1" />
              Agregar Proveedor
            </Button>
          ) : (
            <form onSubmit={handleAddSupplier} className="border border-border rounded-lg p-4 bg-card space-y-3">
              <h4 className="text-sm font-semibold text-foreground">Agregar Proveedor</h4>
              <Select
                label="Proveedor *"
                options={providerOptions}
                value={form?.providerId}
                onChange={v => setForm(prev => ({ ...prev, providerId: v }))}
                placeholder="Seleccionar proveedor"
                searchable
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="SKU Proveedor"
                  placeholder="SKU del proveedor"
                  value={form?.supplierSku}
                  onChange={e => setForm(prev => ({ ...prev, supplierSku: e?.target?.value }))}
                />
                <Input
                  label="Costo"
                  type="number"
                  placeholder="0.00"
                  value={form?.costPrice}
                  onChange={e => setForm(prev => ({ ...prev, costPrice: e?.target?.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Existencia"
                  type="number"
                  placeholder="0"
                  value={form?.availableStock}
                  onChange={e => setForm(prev => ({ ...prev, availableStock: e?.target?.value }))}
                />
                <Input
                  label="Días de Entrega"
                  type="number"
                  placeholder="0"
                  value={form?.leadTimeDays}
                  onChange={e => setForm(prev => ({ ...prev, leadTimeDays: e?.target?.value }))}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" size="sm" onClick={() => setShowAddForm(false)} disabled={saving}>
                  Cancelar
                </Button>
                <Button type="submit" size="sm" loading={saving}>
                  Guardar
                </Button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
};

export default SupplierPanel;
